
			<script lang="ts">
			import layout from '@/views/layouts/App/Main.vue'
			export default { layout }
			</script>
			<template>
		
    <Head>
        <title>{{ trans('Curriculum Connections') }}</title>
    </Head>

    <div class="bg-white">
        <div class="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
            <div class="text-center">
                <h2
                    class="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl"
                >
                    {{ trans('Curriculum Connections') }}
                </h2>

                <p class="mx-auto mt-5 max-w-xl text-xl text-gray-500">
                    {{
                        trans(
                            'Use any combination of the search options to find connections between our Live it Shows and a number of Provincial and State curricula.',
                        )
                    }}
                </p>
            </div>
        </div>
    </div>

    <div class="py-12">
        <div class="mx-auto max-w-screen-2xl px-4 sm:px-6 md:px-8">
            <div class="grid grid-cols-4 items-start gap-8">
                <FormCard @submit="search">
                    <div class="flex flex-col gap-y-2 px-4 py-5 sm:px-6">
                        <ListboxGroup
                            v-model="form.provinces"
                            name="provinces"
                            label="Provinces"
                            multiple
                            tagged
                        >
                            <ListboxOption
                                v-for="province in provinces"
                                :key="province.value"
                                :value="province.value"
                            >
                                {{ province.label }}
                            </ListboxOption>
                        </ListboxGroup>

                        <ListboxGroup
                            v-model="form.programs"
                            :display-value="programDisplayValue"
                            name="programs"
                            label="Programs"
                            multiple
                            tagged
                        >
                            <ListboxOption
                                v-for="program in programs.data"
                                :key="program.sid"
                                :value="program.sid"
                            >
                                {{ program.name }}
                            </ListboxOption>
                        </ListboxGroup>

                        <ListboxGroup
                            v-model="form.subjects"
                            :display-value="subjectDisplayValue"
                            name="subject"
                            label="Subjects"
                            multiple
                            tagged
                        >
                            <ListboxOption
                                v-for="subject in subjects"
                                :key="subject.sid"
                                :value="subject.sid"
                            >
                                {{ subject.course }}
                            </ListboxOption>
                        </ListboxGroup>

                        <ListboxGroup
                            v-model="form.grades"
                            name="grades"
                            label="Grades"
                            multiple
                            tagged
                        >
                            <ListboxOption
                                v-for="grade in grades"
                                :key="grade.level"
                                :value="grade.level"
                            >
                                {{ grade.level }}
                            </ListboxOption>
                        </ListboxGroup>
                    </div>

                    <template #actions>
                        <Button variant="secondary" type="button" @click.prevent="exportCurriculum">
                            Export
                        </Button>

                        <Button type="submit" :loading="form.processing">Search</Button>
                    </template>
                </FormCard>

                <div class="col-span-3 -mt-8">
                    <Table :columns="columns" :resource="bigIdeas" paginated>
                        <template #cell(description)="{ row: bigIdea }">
                            <div class="whitespace-normal">
                                {{ bigIdea.description }}
                            </div>
                        </template>

                        <template #cell(gradeSubjects)="{ row: bigIdea }">
                            <div class="flex flex-col gap-y-1">
                                <div
                                    v-for="gradeSubject in bigIdea.gradeSubjects"
                                    :key="gradeSubject.sid"
                                    class="flex items-center gap-x-3"
                                >
                                    <span class="leading-none">
                                        {{ gradeSubject.subject.course }}
                                    </span>

                                    <span class="leading-none">
                                        [{{ gradeSubject.grade.level }}]
                                    </span>

                                    <Anchor
                                        :href="
                                            route('app.resources.curriculum.competencies', {
                                                bigIdea,
                                                gradeSubject,
                                                programsArray,
                                            })
                                        "
                                        :only="['modal', 'flash']"
                                        :headers="{
                                            'X-LiveIt-Intended-Url': route(
                                                'app.resources.curriculum',
                                            ),
                                        }"
                                        tag="link"
                                        preserve-state
                                        preserve-scroll
                                    >
                                        {{ trans('View') }}
                                    </Anchor>
                                </div>
                            </div>
                        </template>
                    </Table>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useForm, Head } from '@inertiajs/vue3'
    import { usePrograms } from '@/scripts/hooks/app/usePrograms'
    import { useGrades } from '@/scripts/hooks/resources/useGrades'
    import { useSubjects } from '@/scripts/hooks/app/useSubjects'
    import { useQuery } from '@/scripts/hooks/useQuery'
    import {
        GradeModel,
        ProgramModel,
        ResourceCollection,
        SubjectModel,
    } from '@/scripts/types/models'
    import { PaginatedResourceInterface, TableColumnsInterface } from '@/scripts/types/tables'
    import allProvinces from '@/scripts/utils/provinces'
    import Anchor from '@/views/components/Anchor.vue'

    const props = defineProps({
        grades: {
            type: Object as () => ResourceCollection<GradeModel>,
            required: true,
        },
        resourceTypes: {
            type: Object as () => Record<string, string>,
            required: true,
        },
        bigIdeas: {
            type: Object as () => PaginatedResourceInterface,
            required: true,
        },
        programs: {
            type: Object as () => ResourceCollection<ProgramModel>,
            required: true,
        },
        subjects: {
            type: Object as () => ResourceCollection<SubjectModel>,
            required: true,
        },
    })

    // hide drop-down values where we have no data - no point in giving people nothing to search for
    const filterProvinces = (province: { label: string }) =>
        ['British Columbia', 'Ontario', 'New Brunswick'].includes(province.label)
    const filterGrades = () => true
    const filterSubjects = (subject: { course: string }) => subject.course !== 'French (Core)'
    const subjects = props.subjects.data.filter(filterSubjects)
    const provinces = allProvinces.filter(filterProvinces)
    const { grades } = useGrades(props.grades.data.filter(filterGrades))

    const { query } = useQuery()
    const { displayValue: programDisplayValue } = usePrograms(props.programs.data, true)
    const { displayValue: subjectDisplayValue } = useSubjects(subjects, true)
    const form = useForm({
        provinces: query.value.provinces ?? [],
        programs: (query.value.programs as number[])?.map((sid) => Number(sid)) ?? [],
        subjects: (query.value.subjects as number[])?.map((sid) => Number(sid)) ?? [],
        grades: query.value.grades ?? [],
    })

    let programsArray = JSON.stringify(query.value.programs as number[]) ?? []
    const columns: TableColumnsInterface = [
        {
            name: 'description',
            // label: trans('Big Ideas / Overall Expectations') // TODO: fix this later
            label: 'Big Ideas / Overall Expectations',
        },
        {
            name: 'gradeSubjects',
            // label: trans('Subject') // TODO: fix this later
            label: 'Subject',
        },
    ]
    const search = () => {
        form.get(route('app.resources.curriculum'), {
            preserveState: true,
            preserveScroll: true,
            only: ['bigIdeas'],
        }),
            (programsArray = JSON.stringify(form.data().programs as number[]) ?? [])
    }
    const exportCurriculum = () =>
        (location.href = route('app.resources.curriculum.export', form.data()))
</script>
